import { Link } from 'react-router-dom';
import { ArrowRight, Check, Clock, Phone, Calendar, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import { DCA_LINKS } from '@/lib/dcaConfig';
import { careerStrategySession } from '@/data/servicesData';

const CareerStrategySessionPage = () => {
  const service = careerStrategySession;
  const IconComponent = service.icon;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary via-primary to-primary/90 text-white overflow-hidden" data-testid="hero-section">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 text-sm mb-6">
                  <IconComponent className="w-4 h-4" />
                  <span>One-on-One Strategy Session</span>
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-4">
                  {service.title}
                </h1>
                <p className="text-xl md:text-2xl text-white/90 mb-6">
                  {service.tagline}
                </p>
                <p className="text-lg text-white/80 mb-8 max-w-xl">
                  {service.heroDescription}
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button 
                    size="lg" 
                    className="bg-accent hover:bg-accent/90 text-white"
                    data-testid="hero-book-consultation-btn"
                    onClick={() => window.open(DCA_LINKS.free20, '_blank', 'noopener,noreferrer')}
                  >
                    Free 20-Minute Career Strategy Session <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="border-white text-white hover:bg-white/10"
                  >
                    <Phone className="w-5 h-5 mr-2" /> (912) 376-9006
                  </Button>
                </div>
              </div>
              <div className="relative hidden lg:block">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-auto"
                  />
                </div>
                <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-accent rounded-xl"></div>
                <div className="absolute -top-4 -right-4 w-16 h-16 bg-secondary/50 rounded-xl"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Banner */}
        <section className="py-8 bg-accent text-white" data-testid="pricing-banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-center md:text-left">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                  <Clock className="w-8 h-8" />
                </div>
                <div>
                  <p className="text-white/80 text-sm">Session Duration</p>
                  <p className="text-2xl font-bold">{service.pricing.duration}</p>
                </div>
              </div>
              <div className="hidden md:block w-px h-12 bg-white/30"></div>
              <div>
                <p className="text-white/80 text-sm">Investment</p>
                <p className="text-3xl font-bold">${service.pricing.perSession}</p>
              </div>
              <div className="hidden md:block w-px h-12 bg-white/30"></div>
              <div>
                <p className="text-white/80 text-sm">Session Duration</p>
                <p className="text-2xl font-bold">60 Minutes</p>
              </div>
            </div>
          </div>
        </section>

        {/* Is This Right For You */}
        <section className="py-16 bg-muted/30" data-testid="what-it-solves-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
                  Is This Service Right For You?
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  A Career Strategy Session is perfect if you're experiencing any of these challenges:
                </p>
                <ul className="space-y-4">
                  {service.whatItSolves.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center mt-0.5">
                        <Check className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg border">
                <h3 className="text-xl font-heading font-bold mb-4">Quick Stats</h3>
                <div className="grid grid-cols-1 gap-4">
                  {Object.entries(service.stats).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between py-3 border-b last:border-0">
                      <span className="text-muted-foreground capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                      <span className="font-bold text-primary text-lg">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What Your Session Can Cover */}
        <section className="py-16" data-testid="session-coverage-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                What Your Session Can Cover
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Your 60-minute session is completely customized to your needs. Choose any combination of these focus areas:
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {service.sessionCanCover.map((item, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-md border hover:shadow-lg transition-all hover:-translate-y-1"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-primary/10 to-accent/10 rounded-lg flex items-center justify-center mb-4">
                    <Check className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-lg font-heading font-bold mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 bg-muted/30" data-testid="how-it-works-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                How It Works
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {service.processIntro}
              </p>
            </div>

            {/* Process Steps - Timeline */}
            <div className="relative">
              {/* Vertical Line */}
              <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-accent to-secondary transform -translate-x-1/2"></div>
              
              <div className="space-y-8 md:space-y-0">
                {service.steps.map((step, index) => (
                  <div 
                    key={step.number}
                    className={`relative md:flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                  >
                    {/* Content Card */}
                    <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                      <div className={`bg-white rounded-xl p-6 shadow-md border hover:shadow-lg transition-shadow ${index % 2 === 0 ? 'md:mr-8' : 'md:ml-8'} ${step.isFree ? 'ring-2 ring-accent' : ''}`}>
                        <div className={`flex items-center gap-3 mb-3 ${index % 2 === 0 ? 'md:justify-end' : ''}`}>
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-white text-sm font-bold md:hidden">
                            {step.number}
                          </span>
                          <h3 className="text-xl font-heading font-bold">{step.title}</h3>
                          {step.isFree && (
                            <span className="inline-flex items-center gap-1 text-sm text-white bg-accent px-2 py-1 rounded font-medium">
                              FREE
                            </span>
                          )}
                          {step.timeframe && !step.isFree && (
                            <span className="inline-flex items-center gap-1 text-sm text-muted-foreground bg-muted px-2 py-1 rounded">
                              <Clock className="w-3 h-3" />
                              {step.timeframe}
                            </span>
                          )}
                        </div>
                        <p className="text-muted-foreground">{step.description}</p>
                      </div>
                    </div>

                    {/* Center Number Circle */}
                    <div className={`hidden md:flex absolute left-1/2 transform -translate-x-1/2 w-12 h-12 rounded-full text-white items-center justify-center font-bold text-lg shadow-lg z-10 ${step.isFree ? 'bg-accent' : 'bg-gradient-to-br from-primary to-accent'}`}>
                      {step.number}
                    </div>

                    {/* Spacer for opposite side */}
                    <div className="hidden md:block md:w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16" data-testid="benefits-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                Why Choose a Strategy Session?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Get expert guidance without a long-term commitment.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {service.benefits.map((benefit, index) => {
                const BenefitIcon = benefit.icon;
                return (
                  <div 
                    key={index}
                    className="bg-white rounded-xl p-8 shadow-md border hover:shadow-lg transition-all hover:-translate-y-1"
                  >
                    <div className="w-14 h-14 bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl flex items-center justify-center mb-6">
                      <BenefitIcon className="w-7 h-7 text-primary" />
                    </div>
                    <h3 className="text-xl font-heading font-bold mb-3">{benefit.title}</h3>
                    <p className="text-muted-foreground">{benefit.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Investment Section */}
        <section className="py-16 bg-muted/30" data-testid="investment-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                Investment in Your Career
              </h2>
              <p className="text-lg text-muted-foreground">
                One session. Complete clarity. Actionable results.
              </p>
            </div>

            {/* Pricing Card */}
            <div className="bg-white rounded-2xl shadow-xl border overflow-hidden">
              <div className="bg-gradient-to-r from-primary to-primary/90 text-white p-8 text-center">
                <h3 className="text-2xl font-heading font-bold mb-2">{service.title}</h3>
                <div className="flex items-center justify-center gap-2">
                  <span className="text-5xl font-bold">${service.pricing.perSession}</span>
                  <span className="text-white/80">/ 60 minutes</span>
                </div>
              </div>
              
              <div className="p-8">
                <ul className="space-y-4 mb-8">
                  <li className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-primary flex-shrink-0" />
                    <span>60-minute in-depth strategy session</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-primary flex-shrink-0" />
                    <span>Cover any career topic you need</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-primary flex-shrink-0" />
                    <span>Written action plan delivered within 24 hours</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-primary flex-shrink-0" />
                    <span>Personalized recommendations & resources</span>
                  </li>
                </ul>

                <Button 
                  className="w-full" 
                  size="lg"
                  data-testid="pricing-get-started-btn"
                  onClick={() => window.open(DCA_LINKS.free20, '_blank', 'noopener,noreferrer')}
                >
                  Free 20-Minute Career Strategy Session <ArrowRight className="w-5 h-5 ml-2" />
                </Button>

                <p className="text-center text-sm text-muted-foreground mt-4">
                  No commitment required for the consultation.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Coaching Packages Upsell */}
        <section className="py-16" data-testid="packages-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-br from-secondary to-secondary/90 rounded-2xl p-10 text-white">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold mb-4">
                    Need Ongoing Support?
                  </h2>
                  <p className="text-white/90 mb-6">
                    If you're looking for consistent guidance, accountability, and long-term career growth, 
                    explore our coaching packages designed for professionals at every stage.
                  </p>
                  <Button 
                    size="lg" 
                    className="bg-accent hover:bg-accent/90"
                    asChild
                  >
                    <Link to="/coaching/career-coaching-packages">
                      View Coaching Packages <ArrowRight className="w-5 h-5 ml-2" />
                    </Link>
                  </Button>
                </div>
                <div className="hidden lg:flex justify-center">
                  <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm">
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <Calendar className="w-5 h-5" />
                        <span>3, 6, or 12-month programs</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Check className="w-5 h-5" />
                        <span>Biweekly coaching sessions</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Check className="w-5 h-5" />
                        <span>Priority support access</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="bg-secondary text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold mb-2">
                  Ready to Stop Guessing and Start Planning?
                </h2>
                <p className="text-white/80">
                  Your next career move deserves strategy — not uncertainty.
                </p>
              </div>
              <div className="flex gap-4">
                <Button 
                  size="lg" 
                  className="bg-accent hover:bg-accent/90"
                  onClick={() => window.open(DCA_LINKS.free20, '_blank', 'noopener,noreferrer')}
                >
                  Free 20-Minute Career Strategy Session <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-white text-white hover:bg-white/10"
                  asChild
                >
                  <Link to="/contact-us">Contact Us</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CareerStrategySessionPage;
